import { Box, Typography, Stack } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { Link } from '@mui/material'
import ListItem from '@biolumina/ui/components/listItem/listItem'
import List from '@biolumina/ui/components/list/list'
import { StyledIsi } from './isi.styles'
import { Anchor } from '@biolumina/ui/components/anchor/anchor'
import { UNTRUSTED } from '@biolumina/ui/containers/modals/modal.enums'
import { Button } from '@biolumina/ui/components/button/button'
import { useGlobalState } from '@biolumina/ui/context/globalStateProvider'

export const Isi = ({
  children,
  showIndication = true,
  removePartOfTheIsi = false,
}) => {
  const theme = useTheme()
  const { scrollMargin } = useGlobalState()
  const pdfs = {
    prescribingInfo: '/pi.pdf',
    patientInfo: '/ppi.pdf',
  }

  return (
    <StyledIsi className="content-wrapper wide" style={{ paddingTop: 0 }}>
      <Box className="content-wrapper isi">
        {showIndication && (
          <>
            <Typography variant="h3" sx={{ color: 'primary.main' }}>
              What is IBTROZI?
            </Typography>
            <Typography variant="body1">
              IBTROZI<sup>&reg;</sup> (taletrectinib) is a prescription medicine used to treat
              adults with non-small cell lung cancer (NSCLC) that has spread
              within your chest or to other parts of the body and is caused by
              an abnormal <em>ROS1</em> gene.
            </Typography>
            <Typography variant="body1">
              It is not known if IBTROZI is safe and effective in children.
            </Typography>
          </>
        )}

        <Anchor id="isi">
          <Typography color="tertiary.main" variant="h2">
            IMPORTANT SAFETY INFORMATION
          </Typography>
        </Anchor>

        <Typography variant="h3" sx={{ color: 'primary.main' }}>
          What is the most important information I should know about IBTROZI?
        </Typography>
        <Typography variant="body1">
          <strong>IBTROZI can cause serious side effects, including:</strong>
        </Typography>

        <List>
          <ListItem
            alignItems="flex-start"
            sx={{ display: 'list-item', pl: 0 }}
          >
            <Box component="span">
              <strong>Liver problems (Hepatotoxicity).</strong> Changes in liver
              function can happen during treatment with IBTROZI and can lead to
              liver injury and death. Your healthcare provider will do blood
              tests to check your liver function before starting, every 2 weeks
              during the first 2 months of treatment, then monthly as needed
              during your treatment with IBTROZI. Tell your healthcare provider
              right away if you develop signs and symptoms of liver problems,
              including:
            </Box>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={4}
              sx={{ mt: 1, width: '100%' }}
            >
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                <li>
                  yellowing of your skin or the white part of your eyes
                  (jaundice)
                </li>
                <li>dark or "tea-colored" urine</li>
                <li>light-colored stools (bowel movements)</li>
                <li>loss of appetite</li>
              </Box>
              <Box
                component="ul"
                sx={{ m: 0, mt: { xs: '0px !important' }, pl: 2 }}
              >
                <li>nausea or vomiting</li>
                <li>pain on the upper right side of your stomach</li>
                <li>feeling tired or weak</li>
              </Box>
            </Stack>
          </ListItem>

          <ListItem>
            <strong>
              Lung problems (Interstitial Lung Disease/Pneumonitis).
            </strong>{' '}
            IBTROZI can cause lung problems that are severe, life-threatening,
            or lead to death. Tell your healthcare provider right away if you
            have any new or worsening symptoms of lung problems, including
            trouble breathing, shortness of breath, cough (with or without
            mucus), or fever.
          </ListItem>

          <ListItem>
            <strong>
              Changes in the electrical activity of your heart (QT
              prolongation).
            </strong>{' '}
            QT prolongation can cause irregular heartbeats that can be
            life-threatening. Your healthcare provider will do tests before and
            during your treatment with IBTROZI to check the electrical activity
            of your heart and your body salts (electrolytes). Tell your
            healthcare provider right away if you feel faint, lightheaded,
            dizzy, or feel your heart beating irregularly or fast during your
            treatment with IBTROZI. These may be symptoms related to QT
            prolongation.
          </ListItem>

          <ListItem
            alignItems="flex-start"
            sx={{ display: 'list-item', pl: 0 }}
          >
            <Box component="span">
              <strong>
                Increased uric acid level in your blood (Hyperuricemia).
              </strong>{' '}
              Your healthcare provider will check your uric acid blood level
              before and during your treatment with IBTROZI. Your healthcare
              provider may prescribe medicines to lower uric acid if needed.
              Tell your healthcare provider if you develop any of the following
              symptoms of hyperuricemia:
            </Box>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={4}
              sx={{ mt: 1, width: '100%' }}
            >
              <Box component="ul" sx={{ m: 0, pl: 2 }}>
                <li>
                  red, hot, tender, or swollen joints, especially in your big
                  toe
                </li>
                <li>pain in your stomach-area</li>
              </Box>
              <Box
                component="ul"
                sx={{ m: 0, mt: { xs: '0px !important' }, pl: 2 }}
              >
                <li>nausea or vomiting</li>
                <li>pink or brown urine</li>
              </Box>
            </Stack>
          </ListItem>

          <ListItem>
            <strong>Muscle pain, tenderness, and weakness (Myalgia).</strong>{' '}
            IBTROZI can cause myalgia with or without an increase in the level
            of an enzyme in your blood called creatine phosphokinase (CPK),
            which may be a sign of muscle damage. Your healthcare provider will
            do blood tests to check your CPK blood levels every 2 weeks during
            the first month and as needed if you experience unexplained muscle
            pain, tenderness, or weakness with IBTROZI. Tell your healthcare
            provider if you develop any of these symptoms.
          </ListItem>

          <ListItem>
            <strong>Bone fractures.</strong> IBTROZI can increase your risk of
            bone fractures, which may happen with or without a fall or other
            injury. Tell your healthcare provider if you develop pain, changes
            in movement, or bone abnormalities.
          </ListItem>

          <ListItem>
            <strong>Harm to your unborn baby.</strong> IBTROZI should not be
            used in pregnancy. Tell your healthcare provider right away if you
            become pregnant or think you may be pregnant during treatment with
            IBTROZI.
            <List sx={{ mt: 1 }}>
              <ListItem>
                <strong>
                  If you are a female who is able to become pregnant,
                </strong>{' '}
                your healthcare provider should do a pregnancy test before
                starting IBTROZI. Use an effective birth control (contraception)
                during treatment and for 3 weeks after the last dose of IBTROZI.
              </ListItem>
              <ListItem>
                <strong>
                  If you are a male with a female partner who is able to become
                  pregnant,
                </strong>{' '}
                use an effective form of contraception during treatment and for
                3 weeks after the last dose of IBTROZI.
              </ListItem>
            </List>
          </ListItem>
        </List>

        <Typography variant="h3" sx={{ color: 'primary.main', mt: 4 }}>
          What are the most common side effects of IBTROZI?
        </Typography>
        <List>
          <ListItem>
            The most common side effects of IBTROZI include: diarrhea, nausea,
            vomiting, dizziness, rash, constipation, tiredness, changes in your
            liver function tests, and decreased white blood cell levels.
          </ListItem>
        </List>
        <Typography variant="body1">
          These are not all the possible side effects of IBTROZI. Call your
          healthcare provider for more information or medical advice about side
          effects. You may report side effects to FDA at 1-800-FDA-1088 or{' '}
          <Button
		    data-tracking='body-isi-link'
            href="https://www.fda.gov/medwatch"
            prompt={UNTRUSTED}
            component="a"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textDecoration: 'underline',
              color: 'inherit',
              display: 'inline',
              p: 0,
              minWidth: 'auto',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            icon={null}
          >
            www.fda.gov/medwatch
          </Button>
          .
        </Typography>

        <Typography variant="h3" sx={{ color: 'primary.main' }}>
          What should I tell my healthcare provider before starting IBTROZI?
        </Typography>
        <Typography variant="body1">
          <strong>
            Before taking IBTROZI tell your healthcare provider about all your
            medical conditions, including if you:
          </strong>
        </Typography>
        <List>
          <ListItem>have liver problems.</ListItem>
          <ListItem>
            have lung or breathing problems other than lung cancer.
          </ListItem>
          <ListItem>
            have any heart problems, including a condition called long QT
            syndrome.
          </ListItem>
          <ListItem>have gout.</ListItem>
          <ListItem>are pregnant or plan to become pregnant.</ListItem>
          <ListItem>
            are breastfeeding or plan to breastfeed. It is not known if IBTROZI
            passes into your breast milk. Do not breastfeed during treatment and
            for 3 weeks after the last dose of IBTROZI.
          </ListItem>
        </List>

        <Typography variant="body1">
          <strong>
            Tell your healthcare provider about all medicines you take,
          </strong>{' '}
          including prescription and over-the-counter medicines, vitamins, or
          herbal supplements.
        </Typography>

        <Typography variant="h3" sx={{ color: 'primary.main' }}>
          Will medications interact with IBTROZI?
        </Typography>
        <Typography variant="body1">
          IBTROZI may affect the way other medicines work, and other medicines
          may affect how IBTROZI works.
        </Typography>
        <List>
          <ListItem>
            You should not start or stop any medicine before you talk with your
            healthcare provider who prescribed IBTROZI.
          </ListItem>
        </List>

        <Typography variant="h3" sx={{ color: 'primary.main', mt: 4 }}>
          What should I avoid while taking IBTROZI?
        </Typography>
        <List>
          <ListItem>
            Avoid taking proton pump inhibitor (PPI) or H2 blocker medicine. If
            you take an antacid, take it at least 2 hours before or 2 hours
            after taking IBTROZI.
          </ListItem>
          <ListItem>
            You should limit time in the sun during treatment with IBTROZI.
            IBTROZI may make your skin sensitive to sunlight. Wear a hat and
            clothes that cover your skin and use sunscreen with sun protective
            factor (SPF) if you are in the sun during treatment with IBTROZI and
            for at least 5 days after your last dose of IBTROZI.
          </ListItem>
          <ListItem>
            You should avoid grapefruit, grapefruit juice, or products that
            contain grapefruit during your treatment with IBTROZI. Grapefruit
            may increase the amount of IBTROZI in your blood, which may increase
            the risk of IBTROZI side effects.
          </ListItem>
        </List>

        <Typography variant="body1">
          <strong>
            Please read the accompanying{' '}
            <Link
              href="https://ibtrozi-pi.com/patient-information/"
              target="_blank"
              color="primary.main"
			  data-tracking='body-isi-link'
            >
              Patient Information
            </Link>
            .
          </strong>
        </Typography>
      </Box>
      <Box className="content-wrapper">{children}</Box>
    </StyledIsi>
  )
}
